<template>
  <div>
    <Head title="Cutting Output" />
    <div class="flex items-center justify-between mb-6">
      <h1 class="mb-8 text-3xl font-bold">Cutting Output</h1>
      <Link v-if="role === 'admin' || role === 'manager'" class="btn-orange" href="/blocks/cutting-outputs/import">
        <span>Upload Data</span>
        <span class="hidden md:inline">&nbsp;</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow mb-3">
      <form @submit.prevent="store" @keydown.enter="$event.preventDefault()"  @keyup.enter="$event.preventDefault()">
        <div class="flex flex-wrap -mb-2 -mr-6 p-8 w-full lg:w-full">
          <div v-if="form.errors.total_cut_weight && form.errors.cutting_output_volume" class="flex items-center justify-between mb-8 max-w-3xl bg-red-400 rounded w-full lg:w-full">
            <div class="flex items-center">
              <div class="py-4 text-white text-sm font-medium">
                <span class="ml-3">Wrong SKU/Block Weight or Volume </span>
              </div>
            </div>
          </div>
          <div v-if="form.errors.total_cut_weight && !form.errors.cutting_output_volume" class="flex items-center justify-between mb-8 max-w-3xl bg-red-400 rounded w-full lg:w-full">
            <div class="flex items-center">
              <div class="py-4 text-white text-sm font-medium">
                <span class="ml-3">{{ form.errors.total_cut_weight }}</span>
              </div>
            </div>
          </div>
          <div v-if="form.errors.cutting_output_volume && !form.errors.total_cut_weight" class="flex items-center justify-between mb-8 max-w-3xl bg-red-400 rounded w-full lg:w-full">
            <div class="flex items-center">
              <div class="py-4 text-white text-sm font-medium">
                <span class="ml-3">{{ form.errors.cutting_output_volume }}</span>
              </div>
            </div>
          </div>
          <div v-show="!is_reworks" class="pb-2 pr-6 mb-3 w-full lg:w-1/3">
            <Checkbox label="Reject" :active="is_rejected" @input="is_rejected = $event"/>
          </div>
          <div v-show="!is_rejected" class="pb-2 pr-6 mb-3 w-full lg:w-1/3">
            <Checkbox label="Reworks" :active="is_reworks" @input="is_reworks = $event"/>
          </div>
          <div class="pb-2 pr-6 w-full lg:w-full">
            <text-input
              v-model="form.total_cut_weight"
              placeholder="total cut weight"
              class="pb-2 hidden"
            />
            <text-input
              v-model="form.cutting_output_volume"
              placeholder="total cut volume"
              class="pb-2 hidden"
            />
          </div>
          <div v-show="!is_reworks" :class="is_rejected ? 'pb-2 pr-6 w-full lg:w-2/3' : 'pb-2 pr-6 w-full lg:w-1/3'">
            <text-input
              v-model="form.block_number"
              :error="form.errors.block_name"
              placeholder="Block Number"
              class="pb-2"
              @keyup="checkBlockNumber"
            />
            <span
              v-if="blockNumberSearching && form.block_name !== '' && blockNumberObj.message !='' && !form.errors.block_name"
              class="mt-3"
            >
              <span class="form-error">{{ blockNumberObj.message }}</span>
            </span>
            <span v-else>
            </span>
          </div>
          <div v-show="!is_reworks" class="pb-2 pr-6 w-full lg:w-1/3">
            <text-input
              v-model="form.dry_block_weight"
              :error="form.errors.dry_block_weight"
              placeholder="Dry Block Weight"
              class="pb-2"
            />
          </div>
          <div v-show="!is_rejected" v-if="!is_reworks" class="b-2 pr-6 w-full lg:w-1/3">
            <select-input
              v-model="form.line"
              :error="form.errors.line"
              class="pb-2"
            >
              <option :value="null">
                Lines
              </option>
              <option
                v-for="line in lines"
                :key="line.id"
                :value="line.id"
              >
                {{ line.name }}
              </option>
            </select-input>
          </div>
          <div
            v-if="((blockNumberObj.name || is_reworks) && !is_rejected)"
            class="w-full"
          >
            <div
              v-for="(cutting, index) in form.cuttings"
              :key="index"
              class="grid grid-cols-5"
            >
              <div class="pb-2 pr-6 w-full lg:w-4/4">
                <text-input
                  :key="index"
                  v-model="cutting.item_id"
                  :error="form.errors.item_id"
                  class="pb-2"
                  placeholder="Scan Output Part No"
                  @keyup="checkUID"
                />
                <span
                  v-if="(skuObj[`cuttings.${index}.message`] !='' || skuObj[`cuttings.${index}.hide`] =='error') && isValidationErrors(`cuttings.${index}.item_id`)"
                  class="mt-3"
                >
                  <span class="form-error">{{ showValidationErrors(`cuttings.${index}.item_id`) }}</span>
                </span>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-4/4">
                <text-input
                  :key="index"
                  class="pb-2"
                  v-model="skuObj[`cuttings.${index}.sku_size`]"
                  placeholder="Size"
                />
                <span
                  v-if="userSearching && skuObj[`cuttings.${index}.message`] !=''"
                  class="mt-3"
                >
                  <span class="form-error">{{ skuObj[`cuttings.${index}.message`] }}</span>
                </span>
                <span v-else>
                </span>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-4/4">
                <text-input
                  :key="index"
                  class="pb-2"
                  v-model="skuObj[`cuttings.${index}.type`]"
                  placeholder="Part Description"
                />
              </div>
              <div 
                v-if="skuObj[`cuttings.${index}.category`] == 'cushions'"
                class="pb-2 pr-6 w-full lg:w-4/4"
              >
                <select-input
                  v-model="cutting.quality"
                  class="pb-2"
                >
                  <option :value="null">
                    Select Quality
                  </option>
                  <option
                    v-for="quality in qualities"
                    :key="index"
                    :value="quality.name"
                  >
                    {{ quality.name }}
                  </option>
                </select-input>
                <span
                  v-if="isValidationErrors(`cuttings.${index}.quality`)"
                  class="mt-3"
                >
                  <span class="form-error">
                    {{ showValidationErrors(`cuttings.${index}.quality`) }}
                  </span>
                </span>
              </div>
              <div 
                v-if="skuObj[`cuttings.${index}.category`] == 'cushions'"
                class="pb-2 pr-6 w-full lg:w-4/4"
              >
                <select-input
                  v-model="cutting.discount_description_id"
                  class="pb-2"
                >
                  <option :value="null">
                    Select Discount Descriptions
                  </option>
                  <option
                    v-for="discount_description in discount_descriptions"
                    :key="index"
                    :value="discount_description.id"
                  >
                    {{ discount_description.name }}
                  </option>
                </select-input>
                <span
                  v-if="isValidationErrors(`cuttings.${index}.discount_description_id`)"
                  class="mt-3"
                >
                  <span class="form-error">
                    {{ showValidationErrors(`cuttings.${index}.discount_description_id`) }}
                  </span>
                </span>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-4/4">
                <text-input
                  :key="index"
                  class="pb-2"
                  v-model="cutting.quantity"
                  :error="form.errors.quantity"
                  placeholder="Quantity"
                />
                <span
                  v-if="isValidationErrors(`cuttings.${index}.quantity`)"
                  class="mt-3"
                >
                  <span class="form-error">{{ showValidationErrors(`cuttings.${index}.quantity`) }}</span>
                </span>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-4/4">
                <text-input
                  :key="index"
                  class="pb-2"
                  v-model="cutting.weight"
                  :error="form.errors.weight"
                  placeholder="Weight"
                />
                <span
                  v-if="isValidationErrors(`cuttings.${index}.weight`)"
                  class="mt-3"
                >
                  <span class="form-error">{{ showValidationErrors(`cuttings.${index}.weight`) }}</span>
                </span>
              </div>
              <div class="pb-2 pr-6 w-full lg:w-4/4">
                <div class="w-full lg:w-1/2">
                  <svg
                    v-show="form.cuttings.length > 1"
                    @click="removeCutting(index, form.cuttings)"
                    xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="w-full lg:w-1/2">
                  <svg
                    v-show="index === countCuttings"
                    @click="addCutting"
                    xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-full">
              <hr />
            </div>
            <div class="flex flex-wrap -mb-2 -mr-6">
              <div v-if="is_reworks" class="pb-2 pr-6 w-full lg:w-1/2">
                <text-input
                  v-model="form.rework_part_number"
                  :error="form.errors.recycled_part_number"
                  placeholder="Part Number"
                  class="pb-2"
                />
              </div>
              <div v-else class="pb-2 pr-6 w-full lg:w-1/2">
                <text-input
                  v-model="form.recycled_part_number"
                  :error="form.errors.recycled_part_number"
                  placeholder="Part Number"
                  class="pb-2"
                />
              </div>
              <div class="pb-2 pr-6 w-full lg:w-1/2">
                <text-input
                  v-model="form.recycled_weight"
                  :error="form.errors.recycled_weight"
                  placeholder="Weight"
                  class="pb-2"
                />
              </div>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-full">
              <hr />
            </div>
          </div>
          <div
            v-if="blockNumberObj.name || is_reworks"
            class="flex justify-end bg-gray-50 border-t border-gray-100 text-end mt-5"
          >
            <loading-button :loading="form.processing" class="btn-orange" type="submit">Add Output</loading-button>
          </div>
        </div>
      </form>
    </div>
    <hr/>
    <ul class="nav nav-tabs flex flex-col md:flex-row flex-wrap list-none border-b-0 pl-0 mb-4" id="tabs-tabFill"
      role="tablist">
      <li class="nav-item flex-auto text-center" role="presentation">
        <a href="#tabs-homeFill" class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
          active
        " id="tabs-home-tabFill" data-bs-toggle="pill" data-bs-target="#tabs-homeFill" role="tab"
          aria-controls="tabs-homeFill" aria-selected="true">Today's Scans</a>
      </li>
      <li class="nav-item flex-auto text-center" role="presentation">
        <a href="#tabs-profileFill" class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
        " id="tabs-profile-tabFill" data-bs-toggle="pill" data-bs-target="#tabs-profileFill" role="tab"
          aria-controls="tabs-profileFill" aria-selected="false">All Scans</a>
      </li>
    </ul>
    <div class="tab-content" id="tabs-tabContentFill">
      <div class="tab-pane fade show active" id="tabs-homeFill" role="tabpanel" aria-labelledby="tabs-home-tabFill">
        <div class="bg-white rounded-md shadow overflow-x-auto mt-5">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-2 pt-2 px-3">Cutting Date</th>
              <th class="pb-2 pt-2 px-3">Block ID</th>
              <th class="pb-2 pt-2 px-3">Dry Block Weight</th>
              <th class="pb-2 pt-2 px-3">Block Category</th>
              <th class="pb-2 pt-2 px-3">Part Number</th>
              <th class="pb-2 pt-2 px-3">Part Description</th>
              <th class="pb-2 pt-2 px-3">Size</th>
              <th class="pb-2 pt-2 px-3">Quality</th>
              <th class="pb-2 pt-2 px-3">Discount Description</th>
              <th class="pb-2 pt-2 px-3">Quantity</th>
              <th class="pb-2 pt-2 px-3">Weight</th>
              <th class="pb-2 pt-2 px-3">Line</th>
              <th class="pb-2 pt-2 px-3" colspan="2">User</th>
            </tr>
            <tr v-for="item in cuttings.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 focus:text-orange-500" :href="editUrl(item)">
                  {{ item.date }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.block }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.dry_block_weight }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.category }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.part_number }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.part_description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.size }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 text-center" :href="editUrl(item)" tabindex="-1">
                  {{ item.quality }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.discount_description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.quantity }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.weight }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.line }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(item)" tabindex="-1">
                  {{ item.user }}
                </Link>
              </td>
              <td v-if ="role == 'admin' || role == 'manager'" class="w-px border-t">
                <a :href="editUrl(item)" class="flex items-center justify-end pr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </a>
              </td>
            </tr>
            <tr v-if="cuttings.data.length === 0">
              <td class="px-6 py-4 border-t" colspan="5">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="cuttings.links" />
      </div>
      <div class="tab-pane fade" id="tabs-profileFill" role="tabpanel" aria-labelledby="tabs-profile-tabFill">
        <div class="flex items-center justify-between mb-6">
          <date-range v-model="search.dateValue" class="mr-0 w-full max-w-md mr-3"  @reset="reset"/>
          <a
            v-if="cutting_outputs.data.length > 0"
            class="btn-orange px-3 py-2 text-xs cursor-pointer"
            :href="`/blocks/cutting/download/${search.dateValue}`"
            download
            target="__blank"
          >
            <span>Download</span>
          </a>
        </div>
        <div class="bg-white rounded-md shadow overflow-x-auto mt-5">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-2 pt-2 px-3">Cutting Date</th>
              <th class="pb-2 pt-2 px-3">Block ID</th>
              <th class="pb-2 pt-2 px-3">Dry Block Weight</th>
              <th class="pb-2 pt-2 px-3">Block Category</th>
              <th class="pb-2 pt-2 px-3">Part Number</th>
              <th class="pb-2 pt-2 px-3">Part Description</th>
              <th class="pb-2 pt-2 px-3">Size</th>
              <th class="pb-2 pt-2 px-3">Quality</th>
              <th class="pb-2 pt-2 px-3">Discount Description</th>
              <th class="pb-2 pt-2 px-3">Quantity</th>
              <th class="pb-2 pt-2 px-3">Weight</th>
              <th class="pb-2 pt-2 px-3">Line</th>
              <th class="pb-2 pt-2 px-3" colspan="2">User</th>
            </tr>
            <tr v-for="cutting_output in cutting_outputs.data" :key="cutting_output.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 focus:text-orange-500" :href="editUrl(cutting_output)">
                  {{ cutting_output.date }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.block }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.dry_block_weight }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.category }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.part_number }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.part_description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.size }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 text-center" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.quality }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.discount_description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ cutting_output.quantity }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2"  :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.weight }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.line }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" :href="editUrl(cutting_output)" tabindex="-1">
                  {{ cutting_output.user }}
                </Link>
              </td>
              <td v-if ="role == 'admin' || role == 'manager'" class="w-px border-t">
                <a :href="editUrl(cutting_output)" class="flex items-center justify-end pr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </a>
              </td>
            </tr>
            <tr v-if="cutting_outputs.data.length === 0">
              <td class="px-6 py-4 border-t" colspan="5">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="cutting_outputs.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import DateRange from '@/Shared/DateRange'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Pagination from '@/Shared/Pagination'
import Checkbox from '@/Shared/Checkbox'
import axios from 'axios'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    LoadingButton,
    DateRange,
    Pagination,
    Checkbox,
  },
  layout: Layout,
  props: {
    filters: Object,
    cuttings: Object,
    lines: Object,
    qualities: Object,
    discount_descriptions: Object,
    cutting_outputs: Object,
    rework_sku: String,
  },
  data() {
    return {
      is_rejected: false,
      is_reworks: false,
      form: this.$inertia.form({
        block_number: null,
        size: null,
        volume: null,
        sku_volume: null,
        total_cut_weight: 1,
        cutting_output_volume: 1,
        line: null,
        dry_block_weight: null,
        cuttings: [{
            item_id: null,
            cutting_size: null,
            category: null,
            discounted: null,
            quantity: null,
            quality: null,
            discount_description_id: null,
            weight: null,
          }],
        recycled_part_number: null,
        rework_part_number: this.rework_sku,
        recycled_weight: null,
      }),
      itemObj: [],
      blockNumberObj: [],
      userSearching: false,
      blockNumberSearching: false,
      yieldVolume: 0,
      skuObj: [],
      search: {
        dateValue: this.filters.dateValue
      }
    }
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/blocks/cutting/', pickBy(this.search), { preserveState: true })
      }, 150),
    },
  },
  computed: {
    countCuttings() {
      if (this.form.cuttings.length === 0) {
        return 0;
      }
      return this.form.cuttings.length - 1;
    },
    role() {
      return this.$inertia.page.props.auth.user.role
    },
  },
  methods: {
    addCutting(e) {
      e.preventDefault();
      this.form.cuttings.push({
        item_id: null,
        cutting_size: null,
        category: null,
        discounted: null,
        quantity: null,
        quality: null,
        discount_description_id: null,
      });
    },
    isValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return !!error;
      }
      return false;
    },
    showValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return error || '';
      }
      return '';
    },
    sumQuantity() {
      axios.post(`/cutting/yield/search`, this.form).then(response => {
        this.yieldVolume = response.data
      })
    },
    removeCutting(index, cuttings) {
      cuttings.splice(index, 1)
    },
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    editUrl(ele) {
      let url = '#'
      if (this.role == 'admin' || this.role =='manager') {
        if (ele.block_id) {
          url = `/blocks/${ele.block_id}/cutting`
        } else {
          url = `/blocks/${ele.id}/cuttings/edit`
        }        
      }
      return url
    },
    store() {
      this.form.transform(({...data }) => ({
        is_rejected: this.is_rejected,
        is_reworks: this.is_reworks,
        ...data,
      }))
      this.form.post('/blocks/cutting', {
        onSuccess: () => {
          this.form.reset(
            'item_id',
            'block_number',
            'size',
            'cuttings',
            'volume',
            'cutting_size',
            'recycled_part_number',
            'recycled_weight',
            'dry_block_weight',
            'is_reworks',
            'is_rejected'
          )
          this.itemObj = []
          this.blockNumberObj = []
        }
      });
    },
    checkUID() {
      this.userSearching = true
      axios.post(`/cutting/skus/search`, this.form).then(response => {
        this.skuObj = response.data
      })
    },
    checkBlockNumber() {
      this.blockNumberSearching = true
      if (this.form.block_number) {
        axios.get(`/cutting/blocks/${this.form.block_number}/search`).then(response => {
          this.blockNumberObj = response.data
          this.form.size = response.data.size
          this.form.volume = response.data.volume
          this.form.recycled_part_number = response.data.sku_name
        })
      }
    },
  },
}
</script>
